// [PURPOSE]: App Providers - Combined context provider wrapper
// [WHY]: Clean way to wrap entire app with all context providers
// [INTEGRATION]: Single wrapper component for all contexts in correct order
// [CREATED]: October 24, 2025 - Phase 4B: State Migration
// [FIX - NOV 3, 2025]: Added CharacterBridge to safely pass data to PartyProvider

import React from 'react';
import ErrorBoundary from '../components/ErrorBoundary';
import { AuthProvider } from './AuthContext'; // [v1.0]: Firebase Auth
import { SettingsProvider } from './SettingsContext';
import { CharacterProvider, useCharacter } from './CharacterContext';
import { InventoryProvider } from './InventoryContext';
import { PartyProvider } from './PartyContext';
import { UIProvider } from './UIContext';
import { TutorialProvider } from './TutorialContext';
import { ConfirmProvider } from './ConfirmContext';
// [A11Y — 2026-06-20]: Syncs settings.accessibility → data-a11y on <html>.
// Mounted inside SettingsProvider so useSettings() is available.
// Returns null — zero DOM output.
import AppearanceController from '../components/AppearanceController';

/**
 * CharacterBridge - Safely extracts data from CharacterContext and passes to PartyProvider
 *
 * [PURPOSE]: Bridge component that uses useCharacter() hook and passes data as props
 * [WHY]: Prevents PartyProvider from calling useCharacter() before CharacterContext is initialized
 * [PATTERN]: Context Consumer → Props Pattern for nested provider dependencies
 * [FIX - NOV 3 SESSION 3]: Added null checks to prevent "Cannot convert undefined or null to object"
 */
const CharacterBridge = ({ children }) => {
  // [SAFE]: This component is inside CharacterProvider, so useCharacter() works correctly
  const characterContext = useCharacter();

  // [FIX - NOV 3]: Add defensive null checks for playerStats and activityLog
  const playerStats = characterContext?.playerStats || {};
  const activityLog = characterContext?.activityLog || [];

  return (
    <PartyProvider playerStats={playerStats} activityLog={activityLog}>
      {children}
    </PartyProvider>
  );
};

/**
 * AppProviders wraps the entire app with all context providers
 *
 * Provider Order (important for dependencies):
 * 1. ErrorBoundary - Catches all errors
 * 2. AuthProvider - Firebase auth state (no dependencies) [v1.0]
 * 3. SettingsProvider - User preferences (no dependencies)
 * 4. UIProvider - UI state (no dependencies - just modals, tabs, status)
 * 4b. TutorialProvider - Tutorial state (inside UIProvider for setActiveTab, outside CharacterProvider)
 * 5. CharacterProvider - Character state (depends on settings)
 * 6. InventoryProvider - Inventory state (depends on character + UI for showStatus)
 * 7. CharacterBridge - Extracts character data via useCharacter() hook
 * 8. PartyProvider - Party sync (receives playerStats/activityLog via props)
 *
 * [FIX - NOV 3, 2025 - Session 1]:
 * - Added CharacterBridge component to safely extract data from CharacterContext
 * - PartyProvider now receives playerStats/activityLog as props (not via useCharacter)
 * - Prevents context initialization timing issues
 *
 * [FIX - NOV 3, 2025 - Session 2]:
 * - Moved UIProvider ABOVE CharacterProvider and InventoryProvider
 * - InventoryProvider calls useUI() on line 46, so UIProvider must be initialized first
 * - UIProvider has no dependencies, so moving it up is safe
 *
 * Usage:
 * <AppProviders>
 *   <App />
 * </AppProviders>
 */
export const AppProviders = ({ children }) => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SettingsProvider>
          {/* [A11Y]: Controller reads settings, writes data-a11y to <html>. Inert when all flags off. */}
          <AppearanceController />
          <UIProvider>
            <ConfirmProvider>
            <TutorialProvider>
              <CharacterProvider>
                <InventoryProvider>
                  <CharacterBridge>
                    {children}
                  </CharacterBridge>
                </InventoryProvider>
              </CharacterProvider>
            </TutorialProvider>
            </ConfirmProvider>
          </UIProvider>
        </SettingsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default AppProviders;
